import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services';
import { useTranslation } from '../i18n';
import { X, CheckCircle, Loader2, User } from 'lucide-react';

const SHIRTS = ['XS','S','M','L','XL','XXL','XXXL','XXXXL'];const COUNTRIES = [
  { code: 'VN', dial: '+84', flag: '🇻🇳' }, { code: 'SG', dial: '+65', flag: '🇸🇬' },
  { code: 'MY', dial: '+60', flag: '🇲🇾' }, { code: 'TH', dial: '+66', flag: '🇹🇭' },
  { code: 'PH', dial: '+63', flag: '🇵🇭' }, { code: 'ID', dial: '+62', flag: '🇮🇩' },
  { code: 'JP', dial: '+81', flag: '🇯🇵' }, { code: 'KR', dial: '+82', flag: '🇰🇷' },
  { code: 'CN', dial: '+86', flag: '🇨🇳' }, { code: 'HK', dial: '+852', flag: '🇭🇰' },
  { code: 'US', dial: '+1',  flag: '🇺🇸'  },
];
const CAREER_OPTIONS = [
  'Agriculture, Forestry & Fishing','Architecture & Design','Banking & Finance',
  'Business Owner / Entrepreneur','Civil Service / Government','Construction & Engineering',
  'Consulting & Advisory','Education — Teacher / Lecturer','Healthcare — Doctor / Physician',
  'Healthcare — Nurse / Medical Staff','Healthcare — Pharmacist','Healthcare — Dentist / Orthodontist',
  'Hospitality & Tourism','Human Resources','Information Technology','Insurance',
  'Journalism & Media','Law — Lawyer / Legal Professional','Logistics & Supply Chain',
  'Manufacturing & Production','Marketing & Advertising','Military & Defense','Non-profit / NGO',
  'Real Estate','Research & Academia','Retail & Trade','Sales & Business Development',
  'Social Work & Community Services','Sports & Fitness','Transportation & Logistics',
  'Homemaker / Stay-at-home parent','Retired','Other',
];

const inp = 'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all';
const lbl = 'block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5';

// ─── Grade field — 3-scale selector ──────────────────────────────────────────
const GRADE_SCALES_AS = {
  letter: { label: 'A / ABCD', options: ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D+', 'D', 'F'] },
  gpa4:   { label: '4.0 GPA',  options: ['4.0','3.7','3.3','3.0','2.7','2.3','2.0','1.7','1.3','1.0','0.0'] },
  gpa10:  { label: '10-point', options: ['10','9','8','7','6','5','4','3','2','1'] },
};
const detectScale = (val) => {
  if (!val) return 'letter';
  if (GRADE_SCALES_AS.letter.options.includes(val)) return 'letter';
  if (GRADE_SCALES_AS.gpa4.options.includes(val))   return 'gpa4';
  if (GRADE_SCALES_AS.gpa10.options.includes(val))  return 'gpa10';
  return 'letter';
};

function GradeField({ value, onChange }) {
  const [scale, setScale] = useState(() => detectScale(value));
  const handleScaleChange = (m) => { setScale(m); onChange(''); };

  return (
    <div>
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-2">
        {Object.entries(GRADE_SCALES_AS).map(([key, { label }]) => (
          <button key={key} type="button" onClick={() => handleScaleChange(key)}
            className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-semibold transition-all
              ${scale === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>
      <select data-testid="as-grade" className={inp} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Select grade</option>
        {GRADE_SCALES_AS[scale].options.map(g => <option key={g} value={g}>{g}</option>)}
      </select>
    </div>
  );
}

export default function AccountSettingsModal({ onClose }) {
  const { userName, userEmail, familyRole, fetchUserProfile } = useAuth();
  const { t } = useTranslation();
  const isChild = familyRole === 'child';

  const [form, setForm] = useState({
    name: userName || '', dob: '', sex: '',
    dial: '+84', phone: '',
    address: '',
    street: '', district: '', city: '',
    school_name: '', grade: '', tshirt_size: '',
    career: '', company_name: '',
  });
  const [saving, setSaving]     = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [error, setError]       = useState('');

  useEffect(() => {
    authService.getProfile().then(p => {
      if (!p) return;
      // Extract dial code from stored phone (e.g. "+84 90 123")
      const phoneStr = p.phone || '';
      const dialMatch = COUNTRIES.find(c => phoneStr.startsWith(c.dial));
      const dialCode = dialMatch ? dialMatch.dial : '+84';
      const phoneNum = dialMatch ? phoneStr.slice(dialCode.length).trim() : phoneStr;

      setForm(prev => ({
        ...prev,
        name:         p.name || prev.name,
        dob:          p.dob  ? p.dob.split('T')[0] : '',
        sex:          p.sex  || '',
        dial:         dialCode,
        phone:        phoneNum,
        address:      p.address || '',
        street:       p.address || '',
        district:     '',
        city:         '',
        school_name:  p.school_name  || '',
        grade:        p.grade        || '',
        tshirt_size:  p.tshirt_size  || '',
        career:       p.career       || '',
        company_name: p.company_name || '',
      }));
    }).catch(() => {});
  }, []);

  const set = k => e => setForm(prev => ({ ...prev, [k]: typeof e === 'string' ? e : e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSavedMsg('');
    try {
      const payload = { name: form.name };
      if (form.dob)     payload.dob     = form.dob;
      if (form.sex)     payload.sex     = form.sex;
      if (form.phone)   payload.phone   = `${form.dial} ${form.phone}`;
      if (form.street || form.city) payload.address = [form.street, form.district, form.city].filter(Boolean).join(', ');
      // ❌ never send age — backend calculates it from DOB
      if (isChild) {
        if (form.school_name) payload.school_name = form.school_name;
        if (form.grade)       payload.grade       = form.grade;
        if (form.tshirt_size) payload.tshirt_size = form.tshirt_size;
      } else {
        if (form.career)       payload.career       = form.career;
        if (form.company_name) payload.company_name = form.company_name;
      }
      await authService.updateProfile(payload);
      await fetchUserProfile();
      setSavedMsg(t('account.saved'));
      setTimeout(onClose, 1600);
    } catch (err) {
      setError(err?.response?.data?.error_message || err?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div
        data-testid="account-settings-modal"
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-8 pb-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-2xl flex items-center justify-center">
              <User size={18} className="text-sky-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-outfit">{t('account.title')}</h2>
              <p className="text-slate-500 text-xs">{userEmail}</p>
            </div>
          </div>
          <button data-testid="account-settings-close" onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSave} className="px-8 py-6 space-y-4">

          {/* Name */}
          <div>
            <label className={lbl}>{t('account.fields.name')}</label>
            <input data-testid="account-name" className={inp} value={form.name} onChange={set('name')} placeholder="Your full name" />
          </div>

          {/* Gender */}
          <div>
            <label className={lbl}>{t('account.fields.gender')}</label>
            <select data-testid="account-sex" className={inp} value={form.sex} onChange={set('sex')}>
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          {/* Date of Birth */}
          <div>
            <label className={lbl}>{t('account.fields.dob')}</label>
            <input data-testid="account-dob" type="date" className={inp} value={form.dob}
              onChange={set('dob')} max={new Date().toISOString().split('T')[0]} />
          </div>

          {/* Phone */}
          <div>
            <label className={lbl}>{t('account.fields.phone')}</label>
            <div className="flex gap-2">
              <select data-testid="account-dial" className="w-28 shrink-0 rounded-2xl border border-slate-200 bg-slate-50 px-2 py-2.5 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all" value={form.dial} onChange={set('dial')}>
                {COUNTRIES.map(c => <option key={c.code} value={c.dial}>{c.flag} {c.dial}</option>)}
              </select>
              <input data-testid="account-phone" type="tel" className={`${inp} flex-1`} value={form.phone} onChange={set('phone')} placeholder="Phone number" />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-3">
            <label className={lbl}>{t('account.fields.address')}</label>
            <input data-testid="account-street" className={inp} value={form.street} onChange={set('street')} placeholder="Street / House number" />
            <input data-testid="account-district" className={inp} value={form.district} onChange={set('district')} placeholder="District / Ward (optional)" />
            <input data-testid="account-city" className={inp} value={form.city} onChange={set('city')} placeholder="City / Province" />
          </div>

          {/* Child-only */}
          {isChild && (
            <div className="space-y-4 border-t border-slate-100 pt-4">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Academic Info</p>
              <div>
                <label className={lbl}>{t('account.fields.school')}</label>
                <input data-testid="account-school" className={inp} value={form.school_name} onChange={set('school_name')} placeholder="Your school name" />
              </div>
              <div>
                <label className={lbl}>{t('account.fields.grade')}</label>
                <GradeField value={form.grade} onChange={v => setForm(p => ({ ...p, grade: v }))} />
              </div>
              <div>
                <label className={lbl}>{t('account.fields.tshirt')}</label>
                <div className="flex flex-wrap gap-2">
                  {SHIRTS.map(s => (
                    <button key={s} type="button"
                      data-testid={`as-tshirt-${s}`}
                      onClick={() => setForm(p => ({ ...p, tshirt_size: s }))}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border-2
                        ${form.tshirt_size === s ? 'bg-sky-500 text-white border-sky-500' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-sky-300'}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Parent optional fields */}
          {!isChild && (
            <div className="space-y-4 border-t border-slate-100 pt-4">
              <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Professional Info (optional)</p>
              <div>
                <label className={lbl}>Career / Occupation</label>
                <select data-testid="account-career" className={inp} value={form.career} onChange={set('career')}>
                  <option value="">Select your career</option>
                  {CAREER_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Company / Organisation</label>
                <input data-testid="account-company" className={inp} value={form.company_name} onChange={set('company_name')} placeholder="Where you work" />
              </div>
            </div>
          )}

          {/* Feedback */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm flex items-center gap-2">
              <X size={14} /> {error}
            </div>
          )}
          {savedMsg && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-2xl text-green-600 text-sm flex items-center gap-2">
              <CheckCircle size={14} /> {savedMsg}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">
              {t('account.cancel')}
            </button>
            <button data-testid="account-save-btn" type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-2xl font-semibold text-sm transition-all">
              {saving ? <><Loader2 size={14} className="animate-spin" /> {t('account.saving')}</> : t('account.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
